import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Edit, Trash2, Building2, User as UserIcon, X, Check, ChevronsUpDown } from 'lucide-react';
import { User } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete?: (user: User) => void;
  onBulkDelete?: (users: User[]) => void;
  onSchoolTransfer?: (user: User, newSchool: string) => void;
}

interface UserWithRole extends User {
  current_role?: string;
}

export default function UserTable({ users, onEdit, onDelete, onBulkDelete, onSchoolTransfer }: UserTableProps) {
  const [usersWithRoles, setUsersWithRoles] = useState<UserWithRole[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [newSchool, setNewSchool] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [schoolSearchOpen, setSchoolSearchOpen] = useState(false);

  useEffect(() => {
    const loadUserRoles = async () => {
      if (users.length === 0) return;
      
      try {
        const userIds = users.map(u => u.id);
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', userIds)
          .eq('is_active', true);

        const roleMap = new Map();
        rolesData?.forEach(role => {
          roleMap.set(role.user_id, role.role);
        });

        const updatedUsers = users.map(user => ({
          ...user,
          current_role: roleMap.get(user.id) || 'user'
        }));

        setUsersWithRoles(updatedUsers);

        // Extract unique schools for transfer options
        const schools = [...new Set(updatedUsers.map(u => u.school_name).filter(Boolean))];
        setSchoolOptions(schools as string[]);
      } catch (error) {
        console.error('Error loading user roles:', error);
        setUsersWithRoles(users.map(user => ({ ...user, current_role: 'user' })));
      }
    };

    loadUserRoles();
  }, [users]);

  useEffect(() => {
    setShowBulkActions(selectedUsers.size > 0);
  }, [selectedUsers]);

  const handleBulkDelete = async () => {
    if (onBulkDelete && selectedUsers.size > 0) {
      const usersToDelete = usersWithRoles.filter(user => selectedUsers.has(user.id));
      await onBulkDelete(usersToDelete);
      setSelectedUsers(new Set());
    }
  };

  const handleDeleteUser = async (user: UserWithRole) => {
    if (onDelete) {
      await onDelete(user);
    }
  };

  const handleSchoolTransfer = async () => {
    if (selectedUser && newSchool && onSchoolTransfer) {
      await onSchoolTransfer(selectedUser, newSchool);
      setSelectedUser(null);
      setNewSchool('');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(usersWithRoles.map(user => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const isAllSelected = usersWithRoles.length > 0 && selectedUsers.size === usersWithRoles.length;
  const isPartiallySelected = selectedUsers.size > 0 && selectedUsers.size < usersWithRoles.length;

  if (usersWithRoles.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No users found</p>
      </div>
    );
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'coordinator':
        return 'secondary';
      case 'principal':
        return 'outline';
      case 'instructor':
        return 'default';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">
              {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedUsers(new Set())}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
          {onBulkDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedUsers.size})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Multiple Users</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {selectedUsers.size} selected user{selectedUsers.size !== 1 ? 's' : ''}? 
                    This action cannot be undone and will remove all associated data including enrollments, 
                    certificates, and progress records.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleBulkDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected || isPartiallySelected}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>PD Hours</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersWithRoles.map((user) => (
              <TableRow key={user.id} className={selectedUsers.has(user.id) ? 'bg-muted/50' : ''}>
                <TableCell>
                  <Checkbox
                    checked={selectedUsers.has(user.id)}
                    onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                </TableCell>
                <TableCell>{user.email || 'N/A'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {user.school_name || 'N/A'}
                  </div>
                </TableCell>
                <TableCell>{user.department || 'N/A'}</TableCell>
                <TableCell>{user.position_title || user.job_title || 'N/A'}</TableCell>
                <TableCell>
                  <Badge variant={getRoleBadgeVariant(user.current_role || 'user')}>
                    {(user.current_role || 'user').charAt(0).toUpperCase() + (user.current_role || 'user').slice(1)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.pd_hours_completed || 0} / {user.required_pd_hours || 40}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(user)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {onSchoolTransfer && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedUser(user)}
                          >
                            <Building2 className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Transfer to Different School</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-muted-foreground">
                                Transfer {user.full_name || `${user.first_name} ${user.last_name}`} to a different school
                              </p>
                              <p className="text-sm font-medium mt-1">
                                Current: {user.school_name || 'N/A'}
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Select New School</label>
                              <Popover open={schoolSearchOpen} onOpenChange={setSchoolSearchOpen}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={schoolSearchOpen}
                                    className="w-full justify-between"
                                  >
                                    {newSchool ? newSchool : "Search and select school..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0" align="start">
                                  <Command>
                                    <CommandInput placeholder="Search schools..." className="h-9" />
                                    <CommandList className="max-h-[200px]">
                                      <CommandEmpty>No school found.</CommandEmpty>
                                      <CommandGroup>
                                        {schoolOptions
                                          .filter(school => school !== user.school_name)
                                          .map((school) => (
                                            <CommandItem
                                              key={school}
                                              value={school}
                                              onSelect={(currentValue) => {
                                                setNewSchool(currentValue === newSchool ? "" : currentValue);
                                                setSchoolSearchOpen(false);
                                              }}
                                            >
                                              <Building2 className="mr-2 h-4 w-4" />
                                              {school}
                                              <Check
                                                className={`ml-auto h-4 w-4 ${
                                                  newSchool === school ? "opacity-100" : "opacity-0"
                                                }`}
                                              />
                                            </CommandItem>
                                          ))
                                        }
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                            
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => {
                                setSelectedUser(null);
                                setNewSchool('');
                                setSchoolSearchOpen(false);
                              }}>
                                Cancel
                              </Button>
                              <Button onClick={handleSchoolTransfer} disabled={!newSchool}>
                                Transfer
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {onDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {user.full_name || `${user.first_name} ${user.last_name}`}? 
                              This action cannot be undone and will remove all associated data including enrollments, 
                              certificates, and progress records.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteUser(user)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete User
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}